/**
 * Cookie Consent Banner Component
 *
 * GDPR-compliant consent banner for analytics tracking.
 * Shows only when consent hasn't been given yet.
 */

import { useState, useEffect, type FC } from 'react';
import {
    setAnalyticsConsent,
    isConsentPending,
} from '@/lib/telemetry';
import { useCookieConsentBanner } from '@/contexts/CookieConsentContext';

export const CookieConsent: FC = () => {
    const [showBanner, setShowBanner] = useState(false);
    const { setBannerVisible } = useCookieConsentBanner();

    useEffect(() => {
        // Check if consent is pending
        if (isConsentPending()) {
            // Small delay to prevent flash
            const timer = setTimeout(() => {
                setShowBanner(true);
                setBannerVisible(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [setBannerVisible]);

    const handleAccept = () => {
        setAnalyticsConsent(true);
        setShowBanner(false);
        setBannerVisible(false);
    };

    const handleDecline = () => {
        setAnalyticsConsent(false);
        setShowBanner(false);
        setBannerVisible(false);
    };

    if (!showBanner) return null;

    return (
        <div
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-border shadow-lg p-4 z-50"
            role="region"
            aria-label="Cookie consent"
            aria-describedby="cookie-consent-description"
        >
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex-1">
                    <p
                        id="cookie-consent-description"
                        className="text-sm text-foreground"
                    >
                        We use cookies to analyze site usage and improve your experience.
                        By accepting, you consent to analytics cookies.{' '}
                        <a
                            href="/privacy"
                            className="text-primary hover:underline font-medium"
                        >
                            Learn more
                        </a>
                    </p>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={handleDecline}
                        className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                        Decline
                    </button>
                    <button
                        onClick={handleAccept}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
